import { useEffect, useState, useRef, useMemo } from 'react'

import { Button, Checkbox, Dialog, TextField } from '@loadsmart/miranda-react'

import { getStoredUser } from 'auth/infra/StoredUser'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { HttpClient } from 'core/infra/http/HttpClient'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'
import { getSafeTextError } from 'core/utils/safeText'
import { PBIExportableReport } from 'managed-analytics/domain/PBIReport'
import type { Scheduler } from 'managed-analytics/domain/Scheduler'
import { LeadRepository } from 'managed-analytics/infra/LeadRepository'
import { SchedulerRepository } from 'managed-analytics/infra/SchedulerRepository'
import { usePBIExportableReports, usePBIReports } from 'managed-analytics/ui/hooks/usePBIReports'

import {
  StyledCheckboxGrid,
  StyledCheckboxLabel,
  StyledContainer,
  StyledRemoveButton,
  StyledSubmitButton,
  StyledTimeInput,
  StyledTimeItem,
  StyledTimeList,
  CreateGrid,
  FieldError,
  FormRow,
  Label,
  ToggleRow,
  ToggleButton,
  FormSpan,
  GridCol,
  TwoColRow,
  SelectButton,
  ButtonOffset,
  StyledSelect,
} from './Scheduler.styles'
import { TalkToSalesDialog } from './TalkToSalesDialog'
import { TalkToSalesPendingDialog } from './TalkToSalesPendingDialog'
import { getAllTaskTypes, getTaskTypeConfig, registerAllTaskTypes } from './TaskTypeRegistry'

const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const monthDays = Array.from({ length: 31 }, (_, i) => (i + 1).toString())
const timezones = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
]
const legacyTzToIana: Record<string, string> = {
  EST: 'America/New_York',
  CST: 'America/Chicago',
  MST: 'America/Denver',
  PST: 'America/Los_Angeles',
}

interface SchedulerFormProps {
  scheduleToEdit?: Scheduler
  onSubmitComplete?: () => void
  onCancel?: () => void
  onBlockedClose?: () => void
  existingSchedulesCount?: number
}

const SchedulerForm = ({
  scheduleToEdit,
  onSubmitComplete,
  onCancel,
  onBlockedClose,
  existingSchedulesCount = 0,
}: SchedulerFormProps) => {
  // Store payload for deferred POST after lead form
  const pendingSchedulePayload = useRef<Scheduler | null>(null)
  const {
    getExportableReports,
    reports: exportableReports,
    isLoading: isLoadingExportables,
  } = usePBIExportableReports()
  const { getReports, reports: fullReports, isLoading: isLoadingFullReports } = usePBIReports()
  const [selectedProfile, setSelectedProfile] = useState<string | undefined>()
  const [selectedReport, setSelectedReport] = useState<PBIExportableReport | undefined>()
  const [scheduleTimes, setScheduleTimes] = useState<string[]>([])
  const [selectedDays, setSelectedDays] = useState<string[]>([])
  const [recurrenceType, setRecurrenceType] = useState<'daily' | 'weekdays' | 'monthdays'>(
    'weekdays'
  )
  const [timezone, setTimezone] = useState<string>('UTC')
  const allWeekdays = weekdays
  const [exportType, setExportType] = useState<string | undefined>()
  // Removed unused activeAccordion state
  const [taskTypeFormData, setTaskTypeFormData] = useState<any>({})
  const [fullScheduleToEdit, setFullScheduleToEdit] = useState<Scheduler | undefined>(undefined)
  const [name, setName] = useState<string>('')
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [createdScheduleUuid, setCreatedScheduleUuid] = useState('')
  const [schedulerErrorMessage, setSchedulerErrorMessage] = useState<string>('')
  const [isDaysDialogOpen, setIsDaysDialogOpen] = useState(false)
  const [tempSelectedDays, setTempSelectedDays] = useState<string[]>([])
  const [isTimesDialogOpen, setIsTimesDialogOpen] = useState(false)
  const [tempTimes, setTempTimes] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The server rejects markup and template syntax in the subject with a
  // 422; check the same rule here so the user sees which character is the
  // problem while typing.
  const subjectError = useMemo(() => getSafeTextError(name, 'Email subject'), [name])

  const httpClient = new HttpClient(import.meta.env.VITE_BASE_API_URL)
  const schedulerRepository = new SchedulerRepository({ httpClient })
  const { track } = useAnalytics()

  // Helper function to handle cancellation with analytics tracking
  const handleCancel = () => {
    // Track analytics event for form cancellation
    track(AnalyticsEvent.CancelSchedulerForm, {
      'Report Name': selectedReport?.reportName || '',
      'Report Id': selectedReport?.reportId || '',
      'Role Name': selectedReport?.roleName || '',
      'Schedule Type': recurrenceType,
      'Selected Days Count': selectedDays.length,
      'Schedule Times Count': scheduleTimes.length,
      'Timezone': timezone,
      'Export Type': exportType || 'pdf',
      'Is Edit Mode': !!fullScheduleToEdit,
      'Is First Schedule': !fullScheduleToEdit && existingSchedulesCount === 0,
      'Existing Schedules Count': existingSchedulesCount,
    })
    
    onCancel?.()
  }

  // Helper function to handle blocked dialog close - closes everything without cancel tracking
  const handleBlockedDialogClose = () => {
    setIsTalkToSalesPendingDialogOpen(false)
    onBlockedClose?.() // Close the form without success message
  }

  const storage = new LocalStorageClient()
  const user_login = getStoredUser(storage)

  const [isTalkToSalesDialogOpen, setIsTalkToSalesDialogOpen] = useState(false)
  const [isTalkToSalesPendingDialogOpen, setIsTalkToSalesPendingDialogOpen] = useState(false)
  const [isBlockedByLimit] = useState(false)

  useEffect(() => {
    registerAllTaskTypes()
    getExportableReports()
    getReports()
  }, [getExportableReports, getReports])

  const roleNameToProfile: Record<string, string> = useMemo(() => {
    const pairs = fullReports
      .filter(r => r.roleName && r.profile)
      .map(r => [r.roleName, r.profile] as [string, string])
    return Object.fromEntries(pairs)
  }, [fullReports])

  const uniqueProfiles = Array.from(
    new Set(
      exportableReports
        .map(r => roleNameToProfile[r.roleName])
        .filter(Boolean)
    )
  )

  useEffect(() => {
    const fetchScheduleIfNeeded = async () => {
      if (scheduleToEdit && scheduleToEdit.uuid && !scheduleToEdit.eventConfig) {
        const fullSchedule = await schedulerRepository.getSchedule(scheduleToEdit.uuid)

        setFullScheduleToEdit(fullSchedule)
      } else if (scheduleToEdit) {
        setFullScheduleToEdit(scheduleToEdit)
      }
    }

    fetchScheduleIfNeeded()
  }, [scheduleToEdit])

  useEffect(() => {
    if (recurrenceType === 'daily') {
      setSelectedDays(allWeekdays)
      if (scheduleTimes.length === 0) setScheduleTimes(['07:00'])
    } else if (recurrenceType === 'weekdays') {
      // Only keep weekdays
      const filtered = selectedDays.filter(day => allWeekdays.includes(day))
      setSelectedDays(filtered.length ? filtered : ['Mon'])
      if (scheduleTimes.length === 0) setScheduleTimes(['07:00'])
    } else if (recurrenceType === 'monthdays') {
      // Only keep numbers (month days)
      const filtered = selectedDays.filter(day => !isNaN(Number(day)))
      setSelectedDays(filtered.length ? filtered : ['1'])
      if (scheduleTimes.length === 0) setScheduleTimes(['07:00'])
    }
  }, [recurrenceType])

  useEffect(() => {
    if (fullScheduleToEdit) {
      const {
        schedule,
        eventConfig: { report, days, times, timezone, exportType, ...customFields } = {},
      } = fullScheduleToEdit

      setRecurrenceType(schedule === 'monthdays' ? 'monthdays' : 'weekdays')
      setName(fullScheduleToEdit.name ?? '')
      setSelectedReport(report)
      setSelectedProfile(roleNameToProfile[report.roleName])
      setSelectedDays(days)
      setScheduleTimes(times)
      setTimezone(timezone)
      setExportType(exportType)

      if (exportType && getTaskTypeConfig(exportType)) {
        const taskTypeConfig = getTaskTypeConfig(exportType)
        const deserialize = taskTypeConfig?.deserialize
        if (deserialize) {
          setTaskTypeFormData(deserialize(customFields))
        }
      }
    } else if (exportableReports.length > 0) {
      if (!selectedProfile) {
        setSelectedProfile(uniqueProfiles[0])
      }

      if (!exportType) {
        const defaultExportType = getAllTaskTypes()[0]
        setExportType(defaultExportType)
      }
    }
  }, [fullScheduleToEdit, exportableReports, uniqueProfiles, roleNameToProfile])

  const filteredReports = exportableReports.filter(
    report => roleNameToProfile[report.roleName] === selectedProfile
  )

  useEffect(() => {
    if (selectedProfile && !fullScheduleToEdit) {
      setSelectedReport(filteredReports[0])
    }
  }, [selectedProfile, exportableReports, filteredReports, fullScheduleToEdit])

  const handleSubmit = async () => {
    if (subjectError) {
      setErrorDialogOpen(true)
      setSchedulerErrorMessage(subjectError)

      return
    }

    try {
      const taskTypeConfig = getTaskTypeConfig(exportType || 'pdf')
      const normalizedTaskTypeFormData = {
        ...taskTypeFormData,
        file_format:
          (taskTypeFormData as any)?.file_format &&
          String((taskTypeFormData as any).file_format).trim() !== ''
            ? (taskTypeFormData as any).file_format
            : 'pdf',
      }
      const scheduleType = recurrenceType === 'daily' ? 'weekdays' : recurrenceType || 'weekdays'
      const daysToSend =
        recurrenceType === 'monthdays'
          ? selectedDays // only month days
          : recurrenceType === 'daily'
            ? allWeekdays
            : selectedDays
      const emailsToSend =
        user_login?.email && user_login.email.includes('@') ? [user_login.email] : undefined
      const ianaTimezone = legacyTzToIana[timezone] || timezone
      const customFields = taskTypeConfig
        ? taskTypeConfig.serialize(normalizedTaskTypeFormData)
        : {}
      const payload: Scheduler = {
        uuid: fullScheduleToEdit?.uuid,
        name,
        schedule: scheduleType,
        userUuid: selectedReport?.userUuid || '',
        roleUuid: selectedReport?.roleUuid || '',
        eventConfig: {
          report: selectedReport,
          days: daysToSend,
          times: scheduleTimes,
          ...(emailsToSend ? { emails: emailsToSend } : {}),
          timezone: ianaTimezone,
          exportType,
          ...customFields,
        },
      }

      // Track analytics event for form submission
      track(AnalyticsEvent.SubmitSchedulerForm, {
        'Report Name': selectedReport?.reportName || '',
        'Report Id': selectedReport?.reportId || '',
        'Role Name': selectedReport?.roleName || '',
        'Schedule Type': scheduleType,
        'Recurrence Type': recurrenceType,
        'Selected Days Count': daysToSend.length,
        'Schedule Times Count': scheduleTimes.length,
        'Timezone': ianaTimezone,
        'Export Type': exportType || 'pdf',
        'Is Edit Mode': !!fullScheduleToEdit,
        'Is First Schedule': !fullScheduleToEdit && existingSchedulesCount === 0,
        'Existing Schedules Count': existingSchedulesCount,
      })

      // Decide gating based on existence of SchedulerLimit entry for the user's role.
      // If there is NO entry (blocked or not), open lead dialog and defer schedule creation.
      // If we cannot determine the roleUuid, proceed without gating to avoid false positives.
      let shouldOpenLeadDialog = false
      const roleUuid = selectedReport?.roleUuid
      if (roleUuid) {
        try {
          const limitEntry = await schedulerRepository.getScheduleLimitByAuthUserRole(roleUuid)
          // Consider any non-null entry with a uuid as valid regardless of other fields
          shouldOpenLeadDialog = !limitEntry || !limitEntry.uuid
        } catch (e) {
          // On failure to determine, default to showing lead dialog
          shouldOpenLeadDialog = true
        }
      } else {
        // Missing roleUuid; cannot check limit. Do not gate.
        shouldOpenLeadDialog = false
      }

      if (shouldOpenLeadDialog) {
        pendingSchedulePayload.current = payload
        setIsTalkToSalesDialogOpen(true)
        return
      }

      // Otherwise, create the schedule immediately
      setIsSubmitting(true)
      const response = await schedulerRepository.createSchedule(payload)
      console.log('Schedule creation response:', response)
      // Proceed normally when creation succeeds
      // If backend returns schedule: 'blocked', show pending dialog
      if (response && (response as any).schedule === 'blocked') {
        // Track analytics event for blocked scheduler
        track(AnalyticsEvent.SchedulerBlocked, {
          'Report Name': selectedReport?.reportName || '',
          'Report Id': selectedReport?.reportId || '',
          'Role Name': selectedReport?.roleName || '',
          'Schedule Type': scheduleType,
          'Recurrence Type': recurrenceType,
          'Selected Days Count': daysToSend.length,
          'Schedule Times Count': scheduleTimes.length,
          'Timezone': ianaTimezone,
          'Export Type': exportType || 'pdf',
          'Is Edit Mode': !!fullScheduleToEdit,
          'Is First Schedule': !fullScheduleToEdit && existingSchedulesCount === 0,
          'Existing Schedules Count': existingSchedulesCount,
          'Block Reason': 'Schedule limit reached',
        })
        
        setIsTalkToSalesPendingDialogOpen(true)
        setSchedulerErrorMessage((response as any).message || 'Schedule creation blocked.')
        setIsSubmitting(false)
        return
      }
      // If backend returns status: 'error', show error dialog with message
      if (response && (response as any).status === 'error') {
        setErrorDialogOpen(true)
        const msg = (response as any).message
        setSchedulerErrorMessage(
          Array.isArray(msg) ? msg.join('\n') : msg || 'Schedule creation error.'
        )
        setIsSubmitting(false)
        return
      }
      setCreatedScheduleUuid(response.uuid || '')
      setSuccessDialogOpen(true)
      onSubmitComplete?.()
      setIsSubmitting(false)
    } catch (error: any) {
      // Show 'under current evaluation' dialog if status is 'blocked'
      if (error.response?.data?.status === 'blocked') {
        // Track analytics event for blocked scheduler
        track(AnalyticsEvent.SchedulerBlocked, {
          'Report Name': selectedReport?.reportName || '',
          'Report Id': selectedReport?.reportId || '',
          'Role Name': selectedReport?.roleName || '',
          'Schedule Type': recurrenceType === 'daily' ? 'weekdays' : recurrenceType || 'weekdays',
          'Recurrence Type': recurrenceType,
          'Selected Days Count': selectedDays.length,
          'Schedule Times Count': scheduleTimes.length,
          'Timezone': timezone,
          'Export Type': exportType || 'pdf',
          'Is Edit Mode': !!fullScheduleToEdit,
          'Is First Schedule': !fullScheduleToEdit && existingSchedulesCount === 0,
          'Existing Schedules Count': existingSchedulesCount,
          'Block Reason': 'Schedule limit reached',
        })
        
        setIsTalkToSalesPendingDialogOpen(true)
        setSchedulerErrorMessage(error.response?.data?.message || 'Schedule creation blocked.')
        setIsSubmitting(false)
        return
      }
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.non_field_errors?.[0] ||
        JSON.stringify(error.response?.data || {}, null, 2) ||
        'Validation failed. Please check your input.'
      const messageStr = Array.isArray(message) ? message.join('\n') : message
      console.error('Create schedule 422 payload error:', {
        payload: {
          name,
          schedule: recurrenceType,
          userUuid: selectedReport?.userUuid || '',
          roleUuid: selectedReport?.roleUuid || '',
          eventConfig: {
            report: selectedReport,
            days: selectedDays,
            times: scheduleTimes,
            timezone,
            exportType,
            ...taskTypeFormData,
          },
        },
        server: error.response?.data,
      })
      setErrorDialogOpen(true)
      setSchedulerErrorMessage(messageStr)
      setIsSubmitting(false)
    }
  }

  // Extract the form content into a JSX fragment so it can be reused in both create and edit paths

  const formContent = (
    <>
      {isTalkToSalesDialogOpen && (
        <TalkToSalesDialog
          isOpen={isTalkToSalesDialogOpen}
          onClose={() => {
            setIsTalkToSalesDialogOpen(false)
          }}
          reportName={selectedReport?.reportName}
          reportId={selectedReport?.reportId}
          roleName={selectedReport?.roleName}
          onLeadSubmit={async leadValues => {
            if (pendingSchedulePayload.current) {
              try {
                setIsSubmitting(true)
                // Create schedule first
                const response = await schedulerRepository.createSchedule(
                  pendingSchedulePayload.current
                )
                setCreatedScheduleUuid(response.uuid || '')
                // Schedule created successfully; proceed to create lead next
                // Create lead using returned schedule UUID and form values
                const leadPayload = {
                  email: leadValues.email,
                  name: leadValues.name,
                  company: leadValues.company,
                  phone: leadValues.phone,
                  schedule: response.uuid || '',
                  user: selectedReport?.userUuid || '',
                  scheduleObject: response,
                }
                const leadRepo = new LeadRepository({ httpClient })
                await leadRepo.createLead(leadPayload)
                setIsTalkToSalesDialogOpen(false)
                onSubmitComplete?.()
              } catch (error) {
                setErrorDialogOpen(true)
                setSchedulerErrorMessage('Failed to create schedule or lead.')
              } finally {
                setIsSubmitting(false)
              }
              pendingSchedulePayload.current = null
            }
          }}
          userUuid={selectedReport?.userUuid || ''}
          scheduleUuid={createdScheduleUuid}
          schedule={pendingSchedulePayload.current}
        />
      )}
      {isTalkToSalesPendingDialogOpen && (
        <TalkToSalesPendingDialog
          isOpen={isTalkToSalesPendingDialogOpen}
          onClose={handleBlockedDialogClose}
        />
      )}
      <CreateGrid cols={3}>
        <FormSpan span={1}>
          <FormRow>
            <Label>Recurrency</Label>
            <ToggleRow>
              <ToggleButton
                $active={recurrenceType === 'daily'}
                onClick={() => setRecurrenceType('daily')}
              >
                Daily
              </ToggleButton>
              <ToggleButton
                $active={recurrenceType === 'weekdays'}
                onClick={() => setRecurrenceType('weekdays')}
              >
                Weekly
              </ToggleButton>
              <ToggleButton
                $active={recurrenceType === 'monthdays'}
                onClick={() => setRecurrenceType('monthdays')}
              >
                Monthly
              </ToggleButton>
            </ToggleRow>
          </FormRow>
        </FormSpan>

        <FormSpan span={2}>
          <FormRow>
            <Label>Email subject</Label>
            <TextField
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Morning Digest"
              status={subjectError ? 'danger' : undefined}
              style={{ height: '42px', minHeight: 0, padding: '8px 12px', lineHeight: '24px' }}
            />
            {subjectError && <FieldError>{subjectError}</FieldError>}
          </FormRow>
        </FormSpan>

        {/* Timezone + Days + Times row */}
        {recurrenceType === 'daily' ? (
          <>
            <GridCol col={1} span={2}>
              <FormRow>
                <Label>Times</Label>
                <ButtonOffset>
                  <SelectButton
                    onClick={() => {
                      setTempTimes(scheduleTimes.length ? [...scheduleTimes] : ['07:00'])
                      setIsTimesDialogOpen(true)
                    }}
                    style={{ height: '42px', padding: '0 14px', lineHeight: '42px' }}
                  >
                    Select times
                  </SelectButton>
                </ButtonOffset>
              </FormRow>
            </GridCol>
            <GridCol col={3}>
              <FormRow>
                <Label>Timezone</Label>
                <StyledSelect value={timezone} onChange={e => setTimezone(e.target.value)}>
                  {timezones.map(zone => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </StyledSelect>
              </FormRow>
            </GridCol>
          </>
        ) : (
          <>
            <GridCol col={1}>
              <FormRow>
                <Label>{recurrenceType === 'monthdays' ? 'Days of month' : 'Days of week'}</Label>
                <ButtonOffset>
                  <SelectButton
                    onClick={() => {
                      setTempSelectedDays(selectedDays)
                      setIsDaysDialogOpen(true)
                    }}
                    style={{ height: '42px', padding: '0 14px', lineHeight: '42px' }}
                  >
                    Select days
                  </SelectButton>
                </ButtonOffset>
              </FormRow>
            </GridCol>
            <GridCol col={2}>
              <FormRow>
                <Label>Times</Label>
                <ButtonOffset>
                  <SelectButton
                    onClick={() => {
                      setTempTimes(scheduleTimes.length ? [...scheduleTimes] : ['07:00'])
                      setIsTimesDialogOpen(true)
                    }}
                    style={{ height: '42px', padding: '0 14px', lineHeight: '42px' }}
                  >
                    Select times
                  </SelectButton>
                </ButtonOffset>
              </FormRow>
            </GridCol>
            <GridCol col={3}>
              <FormRow>
                <Label>Timezone</Label>
                <StyledSelect value={timezone} onChange={e => setTimezone(e.target.value)}>
                  {timezones.map(zone => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </StyledSelect>
              </FormRow>
            </GridCol>
          </>
        )}

        <FormSpan span={3}>
          {!isLoadingExportables && !isLoadingFullReports && uniqueProfiles.length === 0 ? (
            <FormRow>
              <div style={{ padding: '8px 0', color: '#6b7280' }}>
                Your profile doesn’t have access to export reports. Please talk to sales.
              </div>
            </FormRow>
          ) : (
            <TwoColRow>
              <FormRow>
                <Label>Profile</Label>
                <StyledSelect
                  value={selectedProfile || ''}
                  onChange={e => setSelectedProfile(e.target.value)}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {uniqueProfiles.map(p => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </StyledSelect>
              </FormRow>
              <FormRow>
                <Label>Report</Label>
                {filteredReports.length === 0 ? (
                  <div style={{ padding: '8px 0', color: '#6b7280' }}>
                    No exportable reports available for this profile.
                  </div>
                ) : (
                  <StyledSelect
                    value={selectedReport?.reportId || ''}
                    onChange={e => {
                      const r = filteredReports.find(fr => fr.reportId === e.target.value)
                      if (r) setSelectedReport(r)
                    }}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {filteredReports.map(r => (
                      <option key={r.reportId} value={r.reportId}>
                        {r.reportName}
                      </option>
                    ))}
                  </StyledSelect>
                )}
              </FormRow>
            </TwoColRow>
          )}
        </FormSpan>
      </CreateGrid>
    </>
  )

  const daysDialog = (
    <Dialog open={isDaysDialogOpen} size="large" onClose={() => setIsDaysDialogOpen(false)}>
      <Dialog.Header>
        Select days
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        {recurrenceType === 'monthdays' ? (
          <StyledCheckboxGrid>
            {monthDays.map(day => (
              <StyledCheckboxLabel key={day}>
                <Checkbox
                  checked={tempSelectedDays.includes(day)}
                  onChange={() => {
                    setTempSelectedDays(prev =>
                      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
                    )
                  }}
                >
                  {day}
                </Checkbox>
              </StyledCheckboxLabel>
            ))}
          </StyledCheckboxGrid>
        ) : (
          <StyledCheckboxGrid>
            {weekdays.map(day => (
              <StyledCheckboxLabel key={day}>
                <Checkbox
                  checked={tempSelectedDays.includes(day)}
                  onChange={() => {
                    setTempSelectedDays(prev =>
                      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
                    )
                  }}
                >
                  {day}
                </Checkbox>
              </StyledCheckboxLabel>
            ))}
          </StyledCheckboxGrid>
        )}
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary onClick={() => setIsDaysDialogOpen(false)}>
          Cancel
        </Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          onClick={() => {
            setSelectedDays(tempSelectedDays)
            setIsDaysDialogOpen(false)
          }}
        >
          Apply
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )

  const timesDialog = (
    <Dialog open={isTimesDialogOpen} size="small" onClose={() => setIsTimesDialogOpen(false)}>
      <Dialog.Header>
        Select times
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body
        style={{ display: 'flex', flexDirection: 'column', height: '400px', maxHeight: '400px' }}
      >
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {tempTimes.map((t, idx) => (
            <StyledTimeList key={idx}>
              <StyledTimeItem>
                <StyledTimeInput
                  type="time"
                  value={t}
                  onChange={e => {
                    const val = e.target.value
                    const copy = [...tempTimes]
                    copy[idx] = val
                    setTempTimes(copy)
                  }}
                />
                <StyledRemoveButton
                  onClick={() => setTempTimes(prev => prev.filter((_, i) => i !== idx))}
                >
                  ✖
                </StyledRemoveButton>
              </StyledTimeItem>
            </StyledTimeList>
          ))}
        </div>
        <div style={{ flexShrink: 0 }}>
          <Button onClick={() => setTempTimes(prev => [...prev, '07:00'])} style={{ width: '97%' }}>
            + Add time
          </Button>
        </div>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary onClick={() => setIsTimesDialogOpen(false)}>
          Cancel
        </Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          onClick={() => {
            setScheduleTimes(tempTimes)
            setIsTimesDialogOpen(false)
          }}
        >
          Apply
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )

  // Create flow: render inside a Dialog matching the provided design
  if (!fullScheduleToEdit) {
    return (
      <>
        <Dialog open size="large" onClose={handleCancel}>
          <Dialog.Header>
            Schedule Report
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body>{formContent}</Dialog.Body>
          <Dialog.Actions>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Dialog.ActionTertiary onClick={handleCancel}>Cancel</Dialog.ActionTertiary>
              <Dialog.ActionPrimary
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Schedule Report
              </Dialog.ActionPrimary>
            </div>
          </Dialog.Actions>
        </Dialog>
        {daysDialog}
        {timesDialog}
        <Dialog
          open={errorDialogOpen}
          size="small"
          variant="danger"
          onClose={() => {
            setErrorDialogOpen(false)
          }}
          title="Error"
        >
          <Dialog.Header>
            Error
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body>{schedulerErrorMessage}</Dialog.Body>
          <Dialog.Actions>
            <Dialog.ActionPrimary
              onClick={() => {
                setErrorDialogOpen(false)
              }}
            >
              OK
            </Dialog.ActionPrimary>
          </Dialog.Actions>
        </Dialog>
        {/* Success feedback handled via list banner after closing */}
      </>
    )
  }

  // Edit flow: keep the previous inline layout
  return (
    <StyledContainer>
      {formContent}
      <StyledSubmitButton
        variant="primary"
        loading={isSubmitting}
        disabled={isBlockedByLimit || isSubmitting}
        onClick={handleSubmit}
      >
        Update Schedule
      </StyledSubmitButton>
      <Dialog
        open={successDialogOpen}
        size="small"
        variant="success"
        onClose={() => {
          setSuccessDialogOpen(false)
          onSubmitComplete?.()
        }}
      >
        <Dialog.Header>
          Success
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>Your schedule was successfully created.</Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionPrimary
            onClick={() => {
              setSuccessDialogOpen(false)
              onSubmitComplete?.()
            }}
          >
            OK
          </Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        size="small"
        variant="danger"
        onClose={() => {
          setErrorDialogOpen(false)
        }}
        title="Error"
      >
        <Dialog.Header>
          Error
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>{schedulerErrorMessage}</Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionPrimary
            onClick={() => {
              setErrorDialogOpen(false)
            }}
          >
            OK
          </Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>

      {daysDialog}
      {timesDialog}
    </StyledContainer>
  )
}

export default SchedulerForm
